import asyncForever from 'async/forever';
import asyncSeries from 'async/series';

import { calcChunkSize } from './chunk_size_helper';

import util from '../tools/util';

export default { hashFile };

let g_maxArrayBufferSize = Math.pow(2, 31);
const g_hashCacheMap = new Map();
let g_asmCrypto;

function _load(done) {
  if (g_asmCrypto) {
    done();
  } else {
    import('asmcrypto.js').then(
      (new_module) => {
        g_asmCrypto = new_module;
        done();
      },
      (err) => {
        util.errorLog('HashCache: import err:', err);
        done(err);
      }
    );
  }
}
function hashFile(file, done) {
  const { webkitRelativePath, lastModified, size } = file;
  const key = webkitRelativePath + '-' + lastModified + '-' + size;
  if (g_hashCacheMap.has(key)) {
    done(null, g_hashCacheMap.get(key));
  } else {
    let result;
    asyncSeries(
      [
        _load,
        (done) =>
          _internalHash(file, (err, new_result) => {
            if (!err) {
              result = new_result;
              g_hashCacheMap.set(key, result);
            }
            done(err);
          }),
      ],
      (err) => done(err, result)
    );
  }
}
function _internalHash(file, done) {
  const { size } = file;
  const chunk_size = calcChunkSize(size);
  const part_list: any[] = [];
  let hash;
  let start_pos = 0;
  asyncSeries(
    [
      (done) =>
        asyncForever(
          (done) => {
            const bytes_left = size - start_pos;
            if (bytes_left === 0) {
              done('done');
            } else {
              const part_size = Math.min(chunk_size, bytes_left);
              const opts = {
                file: file.slice(start_pos, start_pos + part_size),
              };
              _hashBlock(opts, (err, part_hash) => {
                start_pos += part_size;
                part_list.push({ part_size, part_hash });
                done(err);
              });
            }
          },
          (err) => {
            if (err === 'done') {
              err = null;
            }
            done(err);
          }
        ),
      (done) => {
        if (part_list.length === 1) {
          hash = part_list[0].part_hash;
          done();
        } else {
          const hash_list = part_list.map((p) => p.part_hash);
          _hashHash(hash_list, (err, result) => {
            hash = result;
            done(err);
          });
        }
      },
    ],
    (err) => done(err, { part_list, hash })
  );
}
function _hashBlock(params, done) {
  const { file } = params;
  if (file.size >= g_maxArrayBufferSize) {
    _hashBlockASM(params, done);
  } else {
    _hashBlockNative(params, (err, hash) => {
      if (err?.name === 'NotReadableError') {
        g_maxArrayBufferSize = file.size - 1;
        _hashBlockASM(params, done);
      } else {
        done(err, hash);
      }
    });
  }
}
function _hashBlockNative(params, done) {
  const { file } = params;
  let buf;
  let hash;
  asyncSeries(
    [
      (done) => {
        file.arrayBuffer().then((result) => {
          buf = result;
          done();
        }, done);
      },
      (done) =>
        window.crypto.subtle.digest('SHA-256', buf).then((result) => {
          hash = _toHex(new Uint8Array(result));
          done();
        }, done),
    ],
    (err) => done(err, hash)
  );
}
function _hashBlockASM(params, done) {
  const { file } = params;
  const block_hash: any = new g_asmCrypto.Sha256();
  const reader = file.stream().getReader();
  asyncForever(
    (done) => {
      reader.read().then(
        (result) => {
          const { value } = result;
          if (value && value.length > 0) {
            block_hash.process(value);
          }
          done(result.done ? 'done' : null);
        },
        (err) => {
          done(err);
        }
      );
    },
    (err) => {
      if (err === 'done') {
        err = null;
      }
      let hash;
      if (!err) {
        block_hash.finish();
        hash = _toHex(block_hash.result);
      }
      done(err, hash);
    }
  );
}
function _hashHash(hash_list, done) {
  const buf = new Uint8Array(hash_list.length * 32);
  hash_list.forEach((hash, i) => {
    buf.set(_fromHex(hash), i * 32);
  });
  window.crypto.subtle.digest('SHA-256', buf).then((result) => {
    done(null, _toHex(new Uint8Array(result)));
  }, done);
}
function _fromHex(str: string): Uint8Array {
  const array = str.match(/../g) || [];
  return Uint8Array.from(array.map((byte) => parseInt(byte, 16)));
}
function _toHex(buf: Uint8Array): string {
  return Array.from(buf)
    .map((i) => ('0' + i.toString(16)).slice(-2))
    .join('');
}
